/**
 * MietwagenAnfrageSection.tsx — Vollständiges Anfrage-Formular für Mietwagen-Akten
 * 
 * Bereiche:
 * 1. Gegnerische Versicherung (Name, E-Mail, Adresse, Telefon, Fax, Schadennummer)
 * 2. Anspruchsteller (Name, Adresse, Telefon, E-Mail, Geburtsdatum)
 * 3. Fahrer (wenn abweichend vom Anspruchsteller)
 * 4. Unfalldaten (Datum, Ort, Hergang, Polizei, Kennzeichen Gegner)
 * 5. Fahrzeugdaten (Kennzeichen, Marke, Modell, Fahrbereit, Totalschaden)
 * 6. Haftung & Kosten (Haftungsquote, Selbstbeteiligung)
 */
import { useState, useCallback, useEffect } from 'react';
import {
  Shield, User, Car, MapPin, Phone, Mail, Calendar,
  Save, Pencil, X, Check, ChevronDown, ChevronUp,
  AlertTriangle, Building2, FileText, UserCheck,
  CreditCard, Scale, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { Akte } from '@/lib/database.types';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface Props {
  akte: Akte;
  onUpdate?: () => void;
}

interface SectionState {
  versicherung: boolean;
  anspruchsteller: boolean;
  fahrer: boolean;
  unfall: boolean;
  fahrzeug: boolean;
  haftung: boolean;
}

// ─── Bekannte Versicherungen für Autocomplete ─────────────────────────────────

const BEKANNTE_VERSICHERUNGEN = [
  'Allianz Versicherungs-AG', 'HUK-COBURG', 'LVM Versicherung', 'Provinzial Versicherung',
  'VHV Allgemeine Versicherung AG', 'DEVK Versicherungen', 'AXA Versicherung AG',
  'HDI Versicherung AG', 'Württembergische Versicherung AG', 'Generali Deutschland AG',
  'ERGO Versicherung AG', 'Zurich Insurance', 'R+V Allgemeine Versicherung AG',
  'Gothaer Allgemeine Versicherung AG', 'Continentale Versicherung', 'Debeka Versicherungen',
  'Signal Iduna Gruppe', 'Nürnberger Versicherung', 'Alte Leipziger Versicherung',
  'Barmenia Versicherungen', 'Concordia Versicherungen', 'Itzehoer Versicherung',
  'Kravag-Allgemeine Versicherung AG', 'Mecklenburgische Versicherung',
  'Öffentliche Versicherung Braunschweig', 'SV SparkassenVersicherung',
  'Versicherungskammer Bayern', 'Westfälische Provinzial', 'Württembergische Gemeinde-Versicherung',
  'Cosmos Direkt', 'DA Direkt', 'AdmiralDirekt', 'Friday Versicherung',
  'Verti Versicherung AG', 'WGV-Versicherung', 'BGV Badische Versicherungen',
  'Landschaftliche Brandkasse Hannover', 'Ostfriesische Landschaftliche Brandkasse',
  'Schleswig-Holsteinische Landesbrandkasse',
];

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function InputField({ label, value, onChange, placeholder, type = 'text', icon: Icon, mono = false, disabled = false }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
  type?: string; icon?: any; mono?: boolean; disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />}
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full text-[12px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors disabled:bg-gray-50 disabled:text-gray-400 ${Icon ? 'pl-8' : ''} ${mono ? 'font-mono' : ''}`}
        />
      </div>
    </div>
  );
}

function ToggleField({ label, value, onChange, description }: {
  label: string; value: boolean; onChange: (v: boolean) => void; description?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-1">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`mt-0.5 w-9 h-5 rounded-full transition-colors flex-shrink-0 relative ${value ? 'bg-orange-500' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'left-[18px]' : 'left-0.5'}`} />
      </button>
      <div>
        <span className="text-[12px] font-medium text-gray-700">{label}</span>
        {description && <p className="text-[10px] text-gray-400 mt-0.5">{description}</p>}
      </div>
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder, rows = 3 }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number;
}) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full text-[12px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors resize-none"
      />
    </div>
  );
}

function SectionHeader({ title, icon: Icon, isOpen, onToggle, badge, badgeColor = 'bg-green-100 text-green-700' }: {
  title: string; icon: any; isOpen: boolean; onToggle: () => void; badge?: string; badgeColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
          <Icon className="w-3.5 h-3.5 text-gray-600" />
        </div>
        <span className="text-[13px] font-bold text-gray-800">{title}</span>
        {badge && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${badgeColor}`}>{badge}</span>
        )}
      </div>
      {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
    </button>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function MietwagenAnfrageSection({ akte, onUpdate }: Props) {
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState<SectionState>({
    versicherung: true,
    anspruchsteller: true,
    fahrer: false,
    unfall: true,
    fahrzeug: false,
    haftung: false,
  });

  // ─── Formular-State ─────────────────────────────────────────────────────────
  // Versicherung
  const [versicherung, setVersicherung] = useState(akte.versicherung || '');
  const [versicherungEmail, setVersicherungEmail] = useState((akte as any).versicherung_email || '');
  const [versicherungTelefon, setVersicherungTelefon] = useState((akte as any).versicherung_telefon || '');
  const [versicherungAdresse, setVersicherungAdresse] = useState((akte as any).versicherung_adresse || '');
  const [versicherungPlz, setVersicherungPlz] = useState((akte as any).versicherung_plz || '');
  const [versicherungOrt, setVersicherungOrt] = useState((akte as any).versicherung_ort || '');
  const [versicherungFax, setVersicherungFax] = useState((akte as any).versicherung_fax || '');
  const [schadennummer, setSchadennummer] = useState(akte.schadennummer || '');
  const [versicherungsnummer, setVersicherungsnummer] = useState(akte.versicherungsnummer || '');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Anspruchsteller
  const [anspruchstellerName, setAnspruchstellerName] = useState((akte as any).anspruchsteller_name || akte.kunde_name || '');
  const [anspruchstellerStrasse, setAnspruchstellerStrasse] = useState((akte as any).anspruchsteller_strasse || '');
  const [anspruchstellerPlz, setAnspruchstellerPlz] = useState((akte as any).anspruchsteller_plz || '');
  const [anspruchstellerOrt, setAnspruchstellerOrt] = useState((akte as any).anspruchsteller_ort || '');
  const [anspruchstellerTelefon, setAnspruchstellerTelefon] = useState((akte as any).anspruchsteller_telefon || akte.kunde_telefon || '');
  const [anspruchstellerEmail, setAnspruchstellerEmail] = useState((akte as any).anspruchsteller_email || akte.kunde_email || '');
  const [anspruchstellerGeburtsdatum, setAnspruchstellerGeburtsdatum] = useState((akte as any).anspruchsteller_geburtsdatum || '');

  // Fahrer
  const [fahrerIstAnspruchsteller, setFahrerIstAnspruchsteller] = useState((akte as any).fahrer_ist_anspruchsteller !== false);
  const [fahrerName, setFahrerName] = useState((akte as any).fahrer_name || '');
  const [fahrerStrasse, setFahrerStrasse] = useState((akte as any).fahrer_strasse || '');
  const [fahrerPlz, setFahrerPlz] = useState((akte as any).fahrer_plz || '');
  const [fahrerOrt, setFahrerOrt] = useState((akte as any).fahrer_ort || '');
  const [fahrerTelefon, setFahrerTelefon] = useState((akte as any).fahrer_telefon || '');
  const [fahrerEmail, setFahrerEmail] = useState((akte as any).fahrer_email || '');
  const [fahrerFuehrerscheinNr, setFahrerFuehrerscheinNr] = useState((akte as any).fahrer_fuehrerschein_nr || '');
  const [fahrerGeburtsdatum, setFahrerGeburtsdatum] = useState((akte as any).fahrer_geburtsdatum || '');

  // Unfalldaten
  const [unfalldatum, setUnfalldatum] = useState((akte as any).unfalldatum || akte.schadendatum || '');
  const [unfallort, setUnfallort] = useState((akte as any).unfallort || akte.schadenort || '');
  const [unfallhergang, setUnfallhergang] = useState((akte as any).unfallhergang || '');
  const [polizeiAufgenommen, setPolizeiAufgenommen] = useState((akte as any).polizei_aufgenommen || false);
  const [polizeiAktenzeichen, setPolizeiAktenzeichen] = useState((akte as any).polizei_aktenzeichen || '');
  const [kennzeichenGegner, setKennzeichenGegner] = useState(akte.kennzeichen_gegner || '');

  // Fahrzeug
  const [kennzeichen, setKennzeichen] = useState(akte.kennzeichen || akte.fahrzeug_kennzeichen || '');
  const [fahrzeugMarke, setFahrzeugMarke] = useState(akte.fahrzeug_marke || '');
  const [fahrzeugModell, setFahrzeugModell] = useState(akte.fahrzeug_modell || '');
  const [fahrzeugFahrbereit, setFahrzeugFahrbereit] = useState((akte as any).fahrzeug_fahrbereit ?? true);
  const [fahrzeugTotalschaden, setFahrzeugTotalschaden] = useState((akte as any).fahrzeug_totalschaden || false);
  const [fahrzeugStandort, setFahrzeugStandort] = useState((akte as any).fahrzeug_standort || '');
  const [fahrzeugKlasse, setFahrzeugKlasse] = useState((akte as any).fahrzeug_klasse || '');

  // Haftung
  const [haftungsquote, setHaftungsquote] = useState(String((akte as any).haftungsquote ?? 100));
  const [selbstbeteiligung, setSelbstbeteiligung] = useState(String((akte as any).selbstbeteiligung ?? 0));

  // Gutachten-Referenz
  const [gutachtenAktenzeichen, setGutachtenAktenzeichen] = useState((akte as any).gutachten_aktenzeichen || '');
  const [gutachtenSv, setGutachtenSv] = useState((akte as any).gutachten_sv || '');

  // Anwalt
  const [anwaltName, setAnwaltName] = useState((akte as any).anwalt_name || '');
  const [anwaltTelefon, setAnwaltTelefon] = useState((akte as any).anwalt_telefon || '');
  const [anwaltEmail, setAnwaltEmail] = useState((akte as any).anwalt_email || '');
  const [anwaltAktenzeichen, setAnwaltAktenzeichen] = useState((akte as any).anwalt_aktenzeichen || '');

  // Werkstatt
  const [werkstattName, setWerkstattName] = useState((akte as any).werkstatt_name || '');
  const [werkstattTelefon, setWerkstattTelefon] = useState((akte as any).werkstatt_telefon || '');

  const toggleSection = (key: keyof SectionState) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Autocomplete-Filter
  const filteredVersicherungen = versicherung.length >= 2
    ? BEKANNTE_VERSICHERUNGEN.filter(v => v.toLowerCase().includes(versicherung.toLowerCase()))
    : [];

  // ─── Speichern ──────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const updateData: Record<string, any> = {
        // Versicherung
        versicherung,
        versicherung_email: versicherungEmail || null,
        versicherung_telefon: versicherungTelefon || null,
        versicherung_adresse: versicherungAdresse || null,
        versicherung_plz: versicherungPlz || null,
        versicherung_ort: versicherungOrt || null,
        versicherung_fax: versicherungFax || null,
        schadennummer: schadennummer || null,
        versicherungsnummer: versicherungsnummer || null,
        // Anspruchsteller
        anspruchsteller_name: anspruchstellerName || null,
        anspruchsteller_strasse: anspruchstellerStrasse || null,
        anspruchsteller_plz: anspruchstellerPlz || null,
        anspruchsteller_ort: anspruchstellerOrt || null,
        anspruchsteller_telefon: anspruchstellerTelefon || null,
        anspruchsteller_email: anspruchstellerEmail || null,
        anspruchsteller_geburtsdatum: anspruchstellerGeburtsdatum || null,
        // Fahrer
        fahrer_ist_anspruchsteller: fahrerIstAnspruchsteller,
        fahrer_name: fahrerIstAnspruchsteller ? null : (fahrerName || null),
        fahrer_strasse: fahrerIstAnspruchsteller ? null : (fahrerStrasse || null),
        fahrer_plz: fahrerIstAnspruchsteller ? null : (fahrerPlz || null),
        fahrer_ort: fahrerIstAnspruchsteller ? null : (fahrerOrt || null),
        fahrer_telefon: fahrerIstAnspruchsteller ? null : (fahrerTelefon || null),
        fahrer_email: fahrerIstAnspruchsteller ? null : (fahrerEmail || null),
        fahrer_fuehrerschein_nr: fahrerIstAnspruchsteller ? null : (fahrerFuehrerscheinNr || null),
        fahrer_geburtsdatum: fahrerIstAnspruchsteller ? null : (fahrerGeburtsdatum || null),
        // Unfalldaten
        unfalldatum: unfalldatum || null,
        unfallort: unfallort || null,
        unfallhergang: unfallhergang || null,
        polizei_aufgenommen: polizeiAufgenommen,
        polizei_aktenzeichen: polizeiAktenzeichen || null,
        kennzeichen_gegner: kennzeichenGegner || null,
        // Fahrzeug
        kennzeichen: kennzeichen || null,
        fahrzeug_marke: fahrzeugMarke || null,
        fahrzeug_modell: fahrzeugModell || null,
        fahrzeug_fahrbereit: fahrzeugFahrbereit,
        fahrzeug_totalschaden: fahrzeugTotalschaden,
        fahrzeug_standort: fahrzeugStandort || null,
        fahrzeug_klasse: fahrzeugKlasse || null,
        // Haftung
        haftungsquote: parseFloat(haftungsquote) || 100,
        selbstbeteiligung: parseFloat(selbstbeteiligung) || 0,
        // Gutachten-Referenz
        gutachten_aktenzeichen: gutachtenAktenzeichen || null,
        gutachten_sv: gutachtenSv || null,
        // Anwalt
        anwalt_name: anwaltName || null,
        anwalt_telefon: anwaltTelefon || null,
        anwalt_email: anwaltEmail || null,
        anwalt_aktenzeichen: anwaltAktenzeichen || null,
        // Werkstatt
        werkstatt_name: werkstattName || null,
        werkstatt_telefon: werkstattTelefon || null,
        // Updated
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('akten')
        .update(updateData)
        .eq('id', akte.id);

      if (error) throw error;
      toast.success('Anfragedaten gespeichert');
      onUpdate?.();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      toast.error('Fehler beim Speichern', { description: err.message });
    } finally {
      setSaving(false);
    }
  }, [
    akte.id, versicherung, versicherungEmail, versicherungTelefon, versicherungAdresse,
    versicherungPlz, versicherungOrt, versicherungFax, schadennummer, versicherungsnummer,
    anspruchstellerName, anspruchstellerStrasse, anspruchstellerPlz, anspruchstellerOrt,
    anspruchstellerTelefon, anspruchstellerEmail, anspruchstellerGeburtsdatum,
    fahrerIstAnspruchsteller, fahrerName, fahrerStrasse, fahrerPlz, fahrerOrt,
    fahrerTelefon, fahrerEmail, fahrerFuehrerscheinNr, fahrerGeburtsdatum,
    unfalldatum, unfallort, unfallhergang, polizeiAufgenommen, polizeiAktenzeichen,
    kennzeichenGegner, kennzeichen, fahrzeugMarke, fahrzeugModell, fahrzeugFahrbereit,
    fahrzeugTotalschaden, fahrzeugStandort, fahrzeugKlasse, haftungsquote, selbstbeteiligung,
    gutachtenAktenzeichen, gutachtenSv, anwaltName, anwaltTelefon, anwaltEmail,
    anwaltAktenzeichen, werkstattName, werkstattTelefon, onUpdate,
  ]);

  // ─── Vollständigkeits-Check ─────────────────────────────────────────────────

  const completeness = {
    versicherung: !!versicherung && !!schadennummer,
    anspruchsteller: !!anspruchstellerName && !!anspruchstellerTelefon,
    fahrer: fahrerIstAnspruchsteller || (!!fahrerName && !!fahrerFuehrerscheinNr),
    unfall: !!unfalldatum && !!unfallort,
    fahrzeug: !!kennzeichen,
    haftung: true,
  };
  const totalComplete = Object.values(completeness).filter(Boolean).length;
  const totalSections = Object.keys(completeness).length;

  return (
    <div className="space-y-3">
      {/* Header mit Fortschritt und Speichern-Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Anfrage & Schadensdaten</h3>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {totalComplete}/{totalSections} Bereiche vollständig
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white rounded-lg text-[11px] font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>

      {/* Fortschrittsbalken */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-orange-500 rounded-full transition-all duration-500"
          style={{ width: `${(totalComplete / totalSections) * 100}%` }}
        />
      </div>

      {/* ═══════ 1. GEGNERISCHE VERSICHERUNG ═══════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Gegnerische Versicherung"
          icon={Shield}
          isOpen={sections.versicherung}
          onToggle={() => toggleSection('versicherung')}
          badge={completeness.versicherung ? 'Vollständig' : 'Unvollständig'}
          badgeColor={completeness.versicherung ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        />
        {sections.versicherung && (
          <div className="p-4 space-y-4 border-t border-gray-100">
            {/* Versicherungsname mit Autocomplete */}
            <div className="relative">
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Versicherung</label>
              <div className="relative">
                <Shield className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={versicherung}
                  onChange={e => { setVersicherung(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="z.B. Allianz, HUK-COBURG, LVM..."
                  className="w-full text-[12px] pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors"
                />
              </div>
              {showSuggestions && filteredVersicherungen.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredVersicherungen.map(v => (
                    <button
                      key={v}
                      type="button"
                      onMouseDown={() => { setVersicherung(v); setShowSuggestions(false); }}
                      className="w-full text-left px-3 py-2 text-[11px] hover:bg-orange-50 transition-colors"
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InputField label="Schadennummer" value={schadennummer} onChange={setSchadennummer} placeholder="z.B. SN-2024-123456" icon={FileText} mono />
              <InputField label="Versicherungsnummer" value={versicherungsnummer} onChange={setVersicherungsnummer} placeholder="Vers.-Nr." icon={CreditCard} mono />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InputField label="E-Mail" value={versicherungEmail} onChange={setVersicherungEmail} placeholder="schaden@versicherung.de" icon={Mail} type="email" />
              <InputField label="Telefon" value={versicherungTelefon} onChange={setVersicherungTelefon} placeholder="+49 ..." icon={Phone} type="tel" />
            </div>

            <InputField label="Straße & Hausnummer" value={versicherungAdresse} onChange={setVersicherungAdresse} placeholder="Musterstr. 1" icon={MapPin} />

            <div className="grid grid-cols-3 gap-3">
              <InputField label="PLZ" value={versicherungPlz} onChange={setVersicherungPlz} placeholder="12345" mono />
              <div className="col-span-2">
                <InputField label="Ort" value={versicherungOrt} onChange={setVersicherungOrt} placeholder="Musterstadt" />
              </div>
            </div>

            <InputField label="Fax" value={versicherungFax} onChange={setVersicherungFax} placeholder="+49 ..." icon={Phone} type="tel" />
          </div>
        )}
      </div>

      {/* ═══════ 2. ANSPRUCHSTELLER ═══════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Anspruchsteller (Geschädigter)"
          icon={User}
          isOpen={sections.anspruchsteller}
          onToggle={() => toggleSection('anspruchsteller')}
          badge={completeness.anspruchsteller ? 'Vollständig' : 'Unvollständig'}
          badgeColor={completeness.anspruchsteller ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        />
        {sections.anspruchsteller && (
          <div className="p-4 space-y-4 border-t border-gray-100">
            <InputField label="Name" value={anspruchstellerName} onChange={setAnspruchstellerName} placeholder="Vor- und Nachname" icon={User} />

            <InputField label="Straße & Hausnummer" value={anspruchstellerStrasse} onChange={setAnspruchstellerStrasse} placeholder="Musterstr. 1" icon={MapPin} />

            <div className="grid grid-cols-3 gap-3">
              <InputField label="PLZ" value={anspruchstellerPlz} onChange={setAnspruchstellerPlz} placeholder="12345" mono />
              <div className="col-span-2">
                <InputField label="Ort" value={anspruchstellerOrt} onChange={setAnspruchstellerOrt} placeholder="Musterstadt" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InputField label="Telefon" value={anspruchstellerTelefon} onChange={setAnspruchstellerTelefon} placeholder="+49 ..." icon={Phone} type="tel" />
              <InputField label="E-Mail" value={anspruchstellerEmail} onChange={setAnspruchstellerEmail} placeholder="email@beispiel.de" icon={Mail} type="email" />
            </div>

            <InputField label="Geburtsdatum" value={anspruchstellerGeburtsdatum} onChange={setAnspruchstellerGeburtsdatum} placeholder="TT.MM.JJJJ" icon={Calendar} />
          </div>
        )}
      </div>

      {/* ═══════ 3. FAHRER ═══════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Fahrer"
          icon={UserCheck}
          isOpen={sections.fahrer}
          onToggle={() => toggleSection('fahrer')}
          badge={completeness.fahrer ? 'Vollständig' : 'Unvollständig'}
          badgeColor={completeness.fahrer ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        />
        {sections.fahrer && (
          <div className="p-4 space-y-4 border-t border-gray-100">
            <ToggleField
              label="Fahrer ist identisch mit Anspruchsteller"
              value={fahrerIstAnspruchsteller}
              onChange={setFahrerIstAnspruchsteller}
              description="Deaktivieren, wenn eine andere Person gefahren ist"
            />

            {!fahrerIstAnspruchsteller && (
              <>
                <InputField label="Name" value={fahrerName} onChange={setFahrerName} placeholder="Vor- und Nachname" icon={User} />
                <InputField label="Straße & Hausnummer" value={fahrerStrasse} onChange={setFahrerStrasse} placeholder="Musterstr. 1" icon={MapPin} />
                <div className="grid grid-cols-3 gap-3">
                  <InputField label="PLZ" value={fahrerPlz} onChange={setFahrerPlz} placeholder="12345" mono />
                  <div className="col-span-2">
                    <InputField label="Ort" value={fahrerOrt} onChange={setFahrerOrt} placeholder="Musterstadt" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Telefon" value={fahrerTelefon} onChange={setFahrerTelefon} placeholder="+49 ..." icon={Phone} type="tel" />
                  <InputField label="E-Mail" value={fahrerEmail} onChange={setFahrerEmail} placeholder="email@beispiel.de" icon={Mail} type="email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Führerschein-Nr." value={fahrerFuehrerscheinNr} onChange={setFahrerFuehrerscheinNr} placeholder="B072..." icon={CreditCard} mono />
                  <InputField label="Geburtsdatum" value={fahrerGeburtsdatum} onChange={setFahrerGeburtsdatum} placeholder="TT.MM.JJJJ" icon={Calendar} />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ═══════ 4. UNFALLDATEN ═══════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Unfalldaten"
          icon={AlertTriangle}
          isOpen={sections.unfall}
          onToggle={() => toggleSection('unfall')}
          badge={completeness.unfall ? 'Vollständig' : 'Unvollständig'}
          badgeColor={completeness.unfall ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        />
        {sections.unfall && (
          <div className="p-4 space-y-4 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Unfalldatum" value={unfalldatum} onChange={setUnfalldatum} type="date" icon={Calendar} />
              <InputField label="Unfallort" value={unfallort} onChange={setUnfallort} placeholder="Stadt / Straße" icon={MapPin} />
            </div>

            <TextAreaField label="Unfallhergang" value={unfallhergang} onChange={setUnfallhergang} placeholder="Kurze Beschreibung des Unfallhergangs..." rows={3} />

            <InputField label="Kennzeichen Unfallgegner" value={kennzeichenGegner} onChange={setKennzeichenGegner} placeholder="XX-XX 1234" icon={Car} mono />

            <ToggleField
              label="Polizeilich aufgenommen"
              value={polizeiAufgenommen}
              onChange={setPolizeiAufgenommen}
              description="Wurde der Unfall von der Polizei aufgenommen?"
            />

            {polizeiAufgenommen && (
              <InputField label="Polizei-Aktenzeichen" value={polizeiAktenzeichen} onChange={setPolizeiAktenzeichen} placeholder="Az. ..." icon={FileText} mono />
            )}
          </div>
        )}
      </div>

      {/* ═══════ 5. FAHRZEUG DES GESCHÄDIGTEN ═══════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Fahrzeug des Geschädigten"
          icon={Car}
          isOpen={sections.fahrzeug}
          onToggle={() => toggleSection('fahrzeug')}
          badge={completeness.fahrzeug ? 'Vollständig' : 'Unvollständig'}
          badgeColor={completeness.fahrzeug ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        />
        {sections.fahrzeug && (
          <div className="p-4 space-y-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-3">
              <InputField label="Kennzeichen" value={kennzeichen} onChange={setKennzeichen} placeholder="XX-XX 1234" icon={Car} mono />
              <InputField label="Marke" value={fahrzeugMarke} onChange={setFahrzeugMarke} placeholder="z.B. VW" />
              <InputField label="Modell" value={fahrzeugModell} onChange={setFahrzeugModell} placeholder="z.B. Golf" />
            </div>

            <InputField label="Fahrzeugklasse" value={fahrzeugKlasse} onChange={setFahrzeugKlasse} placeholder="z.B. Kompaktklasse, Mittelklasse, SUV..." />
            <InputField label="Standort des Fahrzeugs" value={fahrzeugStandort} onChange={setFahrzeugStandort} placeholder="z.B. Werkstatt XY, Abstellplatz..." icon={MapPin} />

            <div className="grid grid-cols-2 gap-3">
              <ToggleField
                label="Fahrzeug fahrbereit"
                value={fahrzeugFahrbereit}
                onChange={setFahrzeugFahrbereit}
                description="Ist das Fahrzeug noch verkehrssicher?"
              />
              <ToggleField
                label="Totalschaden"
                value={fahrzeugTotalschaden}
                onChange={setFahrzeugTotalschaden}
                description="Wirtschaftlicher oder technischer Totalschaden"
              />
            </div>

            {/* Gutachten-Referenz */}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Gutachten-Referenz</p>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Gutachten-Aktenzeichen" value={gutachtenAktenzeichen} onChange={setGutachtenAktenzeichen} placeholder="GA-2024-..." icon={FileText} mono />
                <InputField label="Sachverständiger" value={gutachtenSv} onChange={setGutachtenSv} placeholder="Name des SV" icon={User} />
              </div>
            </div>

            {/* Werkstatt */}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Werkstatt</p>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Werkstatt" value={werkstattName} onChange={setWerkstattName} placeholder="Name der Werkstatt" icon={Building2} />
                <InputField label="Telefon" value={werkstattTelefon} onChange={setWerkstattTelefon} placeholder="+49 ..." icon={Phone} type="tel" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════ 6. HAFTUNG & KOSTEN ═══════ */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Haftung & Kosten"
          icon={Scale}
          isOpen={sections.haftung}
          onToggle={() => toggleSection('haftung')}
          badge={completeness.haftung ? 'Vollständig' : ''}
          badgeColor="bg-green-100 text-green-700"
        />
        {sections.haftung && (
          <div className="p-4 space-y-4 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Haftungsquote (%)</label>
                <div className="relative">
                  <Scale className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={haftungsquote}
                    onChange={e => setHaftungsquote(e.target.value)}
                    className="w-full text-[12px] pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors"
                  />
                </div>
                <p className="text-[9px] text-gray-400 mt-1">100% = volle Haftung des Gegners</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Selbstbeteiligung (€)</label>
                <div className="relative">
                  <CreditCard className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="number"
                    min="0"
                    value={selbstbeteiligung}
                    onChange={e => setSelbstbeteiligung(e.target.value)}
                    className="w-full text-[12px] pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors"
                  />
                </div>
                <p className="text-[9px] text-gray-400 mt-1">Selbstbeteiligung des Geschädigten</p>
              </div>
            </div>

            {/* Anwalt */}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Anwalt</p>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Name" value={anwaltName} onChange={setAnwaltName} placeholder="Name des Anwalts" icon={Scale} />
                <InputField label="Aktenzeichen" value={anwaltAktenzeichen} onChange={setAnwaltAktenzeichen} placeholder="Az. ..." icon={FileText} mono />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <InputField label="Telefon" value={anwaltTelefon} onChange={setAnwaltTelefon} placeholder="+49 ..." icon={Phone} type="tel" />
                <InputField label="E-Mail" value={anwaltEmail} onChange={setAnwaltEmail} placeholder="anwalt@kanzlei.de" icon={Mail} type="email" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Speichern-Button unten */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-6 py-2.5 bg-orange-600 text-white rounded-lg text-[12px] font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 shadow-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Wird gespeichert...' : 'Alle Änderungen speichern'}
        </button>
      </div>
    </div>
  );
}
